import React, { useEffect, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { Select, MenuItem, Button, Typography } from '@mui/material';

import './CreateLobby.scss';

import socket from 'src/components/common/WebSocketConnection/websocket';
import { playSound } from 'src/modules/soundHandler';
import { extractSettings } from 'src/components/gamesCreator/settingsForGames';
import type { GameType } from 'src/components/gamesCreator/GameSquareV2/types';
// eslint-disable-next-line max-len
import { calculateNeededPlayers } from 'src/views/Home/CreateLobby/utils';
import GameSquareV2 from 'src/components/gamesCreator/GameSquareV2';
import { GAMES_LIST } from 'src/games/types';
import type { GameNameType } from 'src/games/types';
import NumberSpinner from 'src/components/common/NumberSpinner';

const CreateLobby = () => {
  const [isPrivateLobby, setIsPrivateLobby] = useState(true);
  const [games, setGames] = useState<GameType[]>([]);
  const [playersNeededState, setPlayersNeededState] = useState('');
  const [playersRequirements, setPlayersRequirements] = useState({
    min: 0,
    max: 0,
  });

  const createLobby = (e: SyntheticEvent) => {
    e.preventDefault();

    const playersNeeded =
      parseInt(playersNeededState) || calculateNeededPlayers(games).max;
    const gamesWithDefault = games.map((game) => ({
      name: game.name,
      settings: extractSettings(game),
    }));

    socket.sendJSON({
      type: 'CREATE_LOBBY',
      payload: {
        playersNeeded,
        games: gamesWithDefault,
        isPrivateLobby,
      },
    });

    playSound('selectClick');
  };

  const addGame = (name: GameNameType) => {
    setGames((prev) => [
      ...prev,
      {
        name,
        settings: {},
      },
    ]);
  };

  const removeGame = (gameNr: number) => {
    setGames((prev) => {
      const updatedGames = [
        ...prev.slice(0, gameNr),
        ...prev.slice(gameNr + 1, prev.length),
      ];
      return updatedGames;
    });
  };

  const updateGameSetting = (
    gameNr: number,
    settingName: string,
    value: string | undefined,
  ) => {
    setGames((prev) => {
      const updateGames = [...prev];

      if (value === undefined) {
        delete updateGames[gameNr].settings[settingName];
        return updateGames;
      }

      updateGames[gameNr].settings[settingName] = value;
      return updateGames;
    });
  };

  const moveAction = (gameNr: number, where: 'left' | 'right') => {
    const newIndex = gameNr + (where === 'left' ? -1 : 1);
    if (games[newIndex]) {
      setGames((prev) => {
        const updateGames = [...prev];
        [updateGames[gameNr], updateGames[newIndex]] = [
          updateGames[newIndex],
          updateGames[gameNr],
        ];
        return updateGames;
      });
    }
  };

  useEffect(() => {
    const neededPlayers = calculateNeededPlayers(games);
    setPlayersRequirements(neededPlayers);
  }, [games]);

  // TODO: Needed game settings to be sent from server?
  return (
    <div className="CreateLobby">
      <>
        <div className="selected-games-holder">
          <div className="selected-games">
            {games.map((game, index) => (
              <GameSquareV2
                key={`${game.name}-${index}`}
                gameNr={index}
                type="selected"
                game={game}
                removeGame={removeGame}
                updateGameSetting={updateGameSetting}
                moveAction={moveAction}
                disabledMoveLeft={!games[index - 1]}
                disabledMoveRight={!games[index + 1]}
              />
            ))}
          </div>
        </div>
        <div className="games-list-title">Available games:</div>
        <div className="games-list-holder">
          <div className="games-list">
            {GAMES_LIST.map((game) => {
              return (
                <GameSquareV2
                  key={game}
                  size="large"
                  type="in-list"
                  game={{
                    name: game,
                    settings: {},
                  }}
                  addGame={addGame}
                />
              );
            })}
          </div>
        </div>
        <div className="lobby-options-holder">
          <form onSubmit={createLobby}>
            <NumberSpinner
              label="Max Players"
              value={
                parseInt(playersNeededState) || playersRequirements.max || 0
              }
              onUpdate={(value) => setPlayersNeededState(String(value))}
              min={playersRequirements.min}
              max={playersRequirements.max}
            />
            <div className="basic-select">
              <Typography
                fontSize="0.7em"
                textTransform="uppercase"
                color="primary"
                marginBottom="8px"
              >
                Lobby Type
              </Typography>
              <Select
                id="lobby-type-select"
                value={isPrivateLobby ? 'private' : 'public'}
                color="primary"
                fullWidth
                sx={{
                  borderRadius: 0,
                  backgroundColor: '#0e0e0e',
                  // TODO: Below not working
                  border: 'none',
                  outline: 'none',
                }}
                onChange={(e) =>
                  setIsPrivateLobby(e.target.value === 'private' ? true : false)
                }
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="public">Public</MenuItem>
              </Select>
            </div>

            <Button
              variant="contained"
              disabled={games.length <= 0}
              type="submit"
            >
              Create lobby
            </Button>
          </form>
        </div>
      </>
    </div>
  );
};

export default CreateLobby;
